export type DetailObjectType = 'customer' | 'contact' | 'opportunity' | 'lead';
export type DetailSectionCategory =
  | 'details'
  | 'collaboration'
  | 'related'
  | 'other';

const DEFINITIONS: Record<
  string,
  { label: string; category: DetailSectionCategory }
> = {
  overview360: { label: '客户概览', category: 'details' },
  advance: { label: '商机推进', category: 'details' },
  details: { label: '详细资料', category: 'details' },
  customerAddress: { label: '客户地址', category: 'details' },
  companyInfo: { label: '工商信息', category: 'details' },
  activities: { label: '活动', category: 'collaboration' },
  followUps: { label: '跟进记录', category: 'collaboration' },
  teamMembers: { label: '协作成员', category: 'collaboration' },
  tasks: { label: '相关任务', category: 'collaboration' },
  businessTrips: { label: '相关出差', category: 'collaboration' },
  contacts: { label: '相关联系人', category: 'related' },
  leads: { label: '相关线索', category: 'related' },
  opportunities: { label: '相关商机', category: 'related' },
  quotes: { label: '相关报价单', category: 'related' },
  contracts: { label: '相关合同', category: 'related' },
  receipts: { label: '相关回款', category: 'related' },
  invoices: { label: '相关发票', category: 'related' },
  products: { label: '相关产品', category: 'related' },
  customerRelation: { label: '客户关系', category: 'related' },
  referrals: { label: '介绍关系', category: 'related' },
  attachments: { label: '附件', category: 'other' },
  expenses: { label: '费用', category: 'other' },
  amountRatio: { label: '金额比例设置', category: 'other' },
  operationLogs: { label: '操作记录', category: 'other' },
};
const DETAIL_KEYS: Record<DetailObjectType, string[]> = {
  customer: ['overview360', 'details'],
  contact: ['details'],
  opportunity: ['advance', 'details'],
  lead: ['details', 'companyInfo'],
};
const CATEGORY_ORDER: DetailSectionCategory[] = [
  'details',
  'collaboration',
  'related',
  'other',
];
export const detailSectionLabel = (key: string, fallback = key): string =>
  DEFINITIONS[key]?.label || fallback;

/** Normalizes display metadata without adding entries or replacing counts/permissions. */
export function normalizeDetailSections<
  T extends { key: string; label: string; enabled?: boolean },
>(objectType: DetailObjectType, sections: readonly T[]): T[] {
  const keys = [
    ...DETAIL_KEYS[objectType],
    ...Object.keys(DEFINITIONS).filter(
      (key) => DEFINITIONS[key].category !== 'details',
    ),
  ];
  const rank = new Map(keys.map((key, index) => [key, index]));
  const unique = new Map(sections.map((section) => [section.key, section]));
  return [...unique.values()]
    .filter(
      (section) =>
        section.enabled !== false &&
        !(
          objectType === 'customer' &&
          ['companyInfo', 'customerAddress', 'customerRelation'].includes(
            section.key,
          )
        ),
    )
    .map((section) => ({
      ...section,
      label: detailSectionLabel(section.key, section.label),
    }))
    .sort((left, right) => {
      const categoryRank = (key: string) =>
        CATEGORY_ORDER.indexOf(DEFINITIONS[key]?.category || 'other');
      return (
        categoryRank(left.key) - categoryRank(right.key) ||
        (rank.get(left.key) ?? keys.length) -
          (rank.get(right.key) ?? keys.length)
      );
    });
}

export function defaultDetailSection<T extends { key: string }>(
  objectType: DetailObjectType,
  sections: readonly T[],
): T['key'] | undefined {
  const preferred = DETAIL_KEYS[objectType][0];
  return (
    sections.find((section) => section.key === preferred)?.key ||
    sections[0]?.key
  );
}
